import { useEffect, useState } from "react";
import { X, Download, Share, Plus } from "lucide-react";

const DISMISS_KEY = "2voto_install_dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return undefined;

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    let timer;
    if (isIOS()) timer = setTimeout(() => setShow(true), 2500);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (isIOS() || !deferred) { setIosHelp(true); return; }
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { console.debug("install prompt dismissed:", e); }
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[120] p-3 sm:p-4 pointer-events-none" data-testid="install-banner">
      <div className="pointer-events-auto mx-auto max-w-2xl rounded-2xl bg-slate-900 text-white shadow-2xl ring-1 ring-white/10 overflow-hidden">
        <div className="flex items-center gap-3 p-4">
          <img src="/logo-mark.png" alt="2Voto" className="w-11 h-11 rounded-xl bg-white/5 p-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-[15px] leading-tight">Instala 2Voto en tu teléfono</p>
            <p className="text-xs text-slate-300 mt-0.5">Acceso rápido, pantalla completa y tu voz siempre a la mano.</p>
          </div>
          <button onClick={install} data-testid="install-button"
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold text-sm px-4 py-2.5 rounded-full transition-colors shrink-0">
            <Download className="w-4 h-4" /> Instalar
          </button>
          <button onClick={dismiss} data-testid="install-dismiss" aria-label="Cerrar"
            className="p-2 text-slate-400 hover:text-white transition-colors shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        {iosHelp && (
          <div className="border-t border-white/10 px-4 py-3 text-sm text-slate-200 bg-white/5" data-testid="install-ios-help">
            <p className="flex items-center flex-wrap gap-1.5">
              En iPhone: toca <Share className="w-4 h-4 inline text-cyan-400" /> <strong>Compartir</strong>
              <span>y luego</span> <Plus className="w-4 h-4 inline text-cyan-400" /> <strong>Agregar a pantalla de inicio</strong>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
